import { useCallback, useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import {
  DEFAULT_CONFIGURATOR_STEP_COPY,
  getAdminConfiguratorStepCopy,
  saveAdminConfiguratorStepCopy,
  type ConfiguratorStepCopyPayload,
  type ConfiguratorStepCopyRow,
} from '../../api';
import {
  CFG_STEP_ADMIN_LABELS,
  CFG_STEP_KEYS,
} from '../../lib/configuratorStepCopy';
import { useHaptic } from '../../hooks/useHaptic';

function mergeCopy(data: ConfiguratorStepCopyPayload): ConfiguratorStepCopyPayload {
  const base = DEFAULT_CONFIGURATOR_STEP_COPY.step_copy;
  const incoming = data.step_copy || {};
  const step_copy: Record<string, ConfiguratorStepCopyRow> = {};
  for (const key of CFG_STEP_KEYS) {
    step_copy[key] = { ...base[key], ...(incoming[key] || {}) };
  }
  return { step_copy };
}

export function ConfiguratorStepCopyPanel() {
  const haptic = useHaptic();
  const [copy, setCopy] = useState<ConfiguratorStepCopyPayload>(DEFAULT_CONFIGURATOR_STEP_COPY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminConfiguratorStepCopy();
      setCopy(mergeCopy(data));
    } catch {
      setCopy(DEFAULT_CONFIGURATOR_STEP_COPY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateStep = (key: string, patch: Partial<ConfiguratorStepCopyRow>) => {
    setCopy((prev) => ({
      step_copy: {
        ...prev.step_copy,
        [key]: { ...prev.step_copy[key], ...patch },
      },
    }));
  };

  const handleSave = async () => {
    haptic.selection();
    setSaving(true);
    setMessage(null);
    try {
      const saved = await saveAdminConfiguratorStepCopy(copy);
      setCopy(mergeCopy(saved));
      haptic.success();
      setMessage('Подсказки конфигуратора сохранены.');
    } catch {
      haptic.error();
      setMessage('Не удалось сохранить.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-xs py-4 text-center" style={{ color: 'var(--text-3)' }}>Загрузка…</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
        Серые блоки над кнопками на карточке товара: «Профиль исполнения», «Питание мотора», «Направление открывания» и т.д.
        Действуют для всех раздвижных и похожих конфигураторов. Переопределение для одного подтипа — в каталоге (option_schema.step_copy).
      </p>
      <div className="space-y-3">
        {CFG_STEP_KEYS.map((key) => {
          const row = copy.step_copy[key] || DEFAULT_CONFIGURATOR_STEP_COPY.step_copy[key];
          return (
            <div
              key={key}
              className="rounded-xl p-3 space-y-2"
              style={{ border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.03)' }}
            >
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--cyan)' }}>
                {CFG_STEP_ADMIN_LABELS[key] || key}
              </p>
              <div className="flex gap-2 items-end">
                <label className="block shrink-0 w-14">
                  <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Иконка</span>
                  <input
                    value={row.icon}
                    onChange={(e) => updateStep(key, { icon: e.target.value })}
                    className="input-glass w-full rounded-xl px-2 py-2 text-sm mt-0.5 text-center"
                    maxLength={4}
                  />
                </label>
                <label className="block flex-1">
                  <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Заголовок блока</span>
                  <input
                    value={row.title}
                    onChange={(e) => updateStep(key, { title: e.target.value })}
                    className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Подсказка (текст над кнопками)</span>
                <textarea
                  value={row.hint}
                  rows={3}
                  onChange={(e) => updateStep(key, { hint: e.target.value })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5 resize-none"
                />
              </label>
            </div>
          );
        })}
      </div>
      {message && (
        <p className="text-xs px-1" style={{ color: message.includes('сохран') ? '#9AE6B4' : '#FC814A' }}>
          {message}
        </p>
      )}
      <button
        type="button"
        disabled={saving}
        onClick={() => void handleSave()}
        className="btn-glow w-full text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2"
      >
        <Save size={16} />
        {saving ? 'Сохранение…' : 'Сохранить подсказки конфигуратора'}
      </button>
    </div>
  );
}
